import { createContext, useContext } from 'react';

import { useDeploymentState } from 'stream/deployments/useDeploymentState';

import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';
import type { RootState } from '@polyai/stream/store';

import { useAppSelector } from 'hooks/reduxHooks';

const PreviewVersionContext = createContext<RootState | null>(null);

export const usePreviewVersionState = () => useContext(PreviewVersionContext);

export function DeploymentVersionProvider({
  deploymentId,
  children,
}: {
  children: React.ReactNode;
  deploymentId: string;
}) {
  const { data: deploymentState } = useDeploymentState({
    deploymentId,
  });

  return (
    <ConditionalWrapper
      condition={!!deploymentId && !!deploymentState?.projection}
      wrapper={children => (
        <PreviewVersionContext.Provider
          value={deploymentId ? (deploymentState?.projection ?? null) : null}
        >
          {children}
        </PreviewVersionContext.Provider>
      )}
    >
      {children}
    </ConditionalWrapper>
  );
}

export function ExplicitVersionProvider({
  deployment,
  children,
}: {
  children: React.ReactNode;
  deployment?: RootState;
}) {
  return (
    <ConditionalWrapper
      condition={!!deployment}
      wrapper={children => (
        <PreviewVersionContext.Provider value={deployment ?? null}>
          {children}
        </PreviewVersionContext.Provider>
      )}
    >
      {children}
    </ConditionalWrapper>
  );
}

export function PreviewVersionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const previewDeploymentId = useAppSelector(
    state => state.project.preview.deploymentId,
  );

  const { data: deploymentState } = useDeploymentState({
    deploymentId: previewDeploymentId,
  });

  return (
    <PreviewVersionContext.Provider
      value={previewDeploymentId ? (deploymentState?.projection ?? null) : null}
    >
      {children}
    </PreviewVersionContext.Provider>
  );
}
